import * as vp from 'vopay';
import { JSX, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './NavigationMenu.module.scss';

import type { SupportedModules } from '../../../providers/FeatureFlag.provider';
import useActiveModule from '../../../hooks/useActiveModule';
import { useNavMenu } from '../../../providers/NavMenu.provider';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';
import { useOpenModule } from '../../../providers/OpenModule.provider';
import { useLockedFeatureDialogue } from '../../../providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '../../../providers/LockedFeatures.provider';

import { useBranding } from '../../../providers/Branding.provider';

const { Box, NavModule } = vp;

type NavigationMenuType = {
  expandMenu?: boolean;
};

const PARENT_MODULES = new Set<SupportedModules>([
  'myAccount',
  'transactionApproval',
  'subAccounts',
  'clientAccounts',
  'eLinx',
  'payment',
  'collection',
  'globalSend',
  'contacts',
  'software',
  'statements',
  'support',
]);

const NavigationMenu = ({ expandMenu }: NavigationMenuType): JSX.Element => {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.NavigationMenu' });
  const history = useHistory();
  const { activeModule } = useActiveModule();
  const { getSubModules, onNavClick, onSubNavClick } = useNavMenu();
  const { openModule, setOpenModule } = useOpenModule();
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const { pathname } = useLocation();

  const { secondaryColor } = useBranding();
  const SubAccountLocked: boolean = checkIsLocked('subAccounts');
  const subModuleMap: {
    [key: string]:
      | { id?: string; title?: string; path?: string; activeModuleId?: string; subNavModules: never[] }[]
      | never[];
  } = {
    dashboard: [],
    myAccount: [
      {
        id: 'navModule-accountSummary',
        title: t('accountBalances') || '',
        path: '/account-summary',
        activeModuleId: 'accountSummary',
        subNavModules: [],
      },
      {
        id: 'navModule-accountAndBilling',
        title: t('accountAndBilling') || '',
        path: '/account-and-billing',
        activeModuleId: 'accountAndBilling',
        subNavModules: [],
      },
      {
        id: 'navModule-fundMyAccount',
        title: t('fundMyAccount') || '',
        path: '/fund-my-account',
        activeModuleId: 'fundMyAccount',
        subNavModules: [],
      },
      {
        id: 'navModule-withdrawMyAccount',
        title: t('withdrawMyAccount') || '',
        path: '/withdraw-my-account',
        activeModuleId: 'withdrawMyAccount',
        subNavModules: [],
      },
      {
        id: 'navModule-bankAccountTransfer',
        title: t('bankAccountTransfer') || '',
        path: '/bank-account-transfer',
        activeModuleId: 'bankAccountTransfer',
        subNavModules: [],
      },
      {
        id: 'navModule-myBankAccounts',
        title: t('operationalAccounts') || '',
        path: '/operational-accounts',
        activeModuleId: 'myBankAccounts',
        subNavModules: [],
      },
      {
        id: 'navModule-linkMyAccount',
        title: t('linkMyAccount') || '',
        path: '/link-my-account',
        activeModuleId: 'myBankAccounts',
        subNavModules: [],
      },
      {
        id: 'navModule-autoBalanceTransfer',
        title: t('autoBalanceTransfer') || '',
        path: '/auto-balance-transfer',
        activeModuleId: 'autoBalanceTransfer',
        subNavModules: [],
      },
    ],
    transactionApproval: [
      {
        id: 'navModule-approvalInbox',
        title: t('approvalInbox') || '',
        path: '/transaction-approval/inbox',
        activeModuleId: 'approvalInbox',
        subNavModules: [],
      },
      {
        id: 'navModule-approvalInbox',
        title: t('approvalList') || '',
        path: '/transaction-approval/approval-list',
        activeModuleId: 'approvalList',
        subNavModules: [],
      },
      {
        id: 'navModule-approvalList',
        title: t('configuration') || '',
        path: '/transaction-approval/configuration',
        activeModuleId: 'approvalConfiguration',
        subNavModules: [],
      },
    ],
    subAccounts: [
      {
        id: 'navModule-createSubAccount',
        title: t('createSubAccount') || '',
        path: '/create-sub-account',
        activeModuleId: 'createSubAccount',
        subNavModules: [],
      },
      {
        id: 'navModule-subAccountsList',
        title: t('subAccountsList') || '',
        path: '/sub-accounts-list',
        activeModuleId: 'subAccountsList',
        subNavModules: [],
      },
      {
        id: 'navModule-accountTransfer',
        title: t('accountTransfer') || '',
        path: '/sub-account-transfer',
        activeModuleId: 'accountTransfer',
        subNavModules: [],
      },
    ],
    clientAccounts: [
      {
        id: 'navModule-addNewClientAccount',
        title: t('addNewClientAccount') || '',
        path: '/add-client-account',
        activeModuleId: 'addNewClientAccount',
        subNavModules: [],
      },
      {
        id: 'navModule-clientAccountsList',
        title: t('clientAccountsList') || '',
        path: '/client-accounts',
        activeModuleId: 'clientAccountsList',
        subNavModules: [],
      },
      {
        id: 'navModule-clientTransferFunds',
        title: t('transferFunds') || '',
        path: '/client-account-transfer-funds',
        activeModuleId: 'transferFunds',
        subNavModules: [],
      },
      {
        id: 'navModule-clientAccountSchedule',
        title: t('scheduledClientAccountTransfers') || '',
        path: '/client-account-schedule',
        activeModuleId: 'clientAccountSchedule',
        subNavModules: [],
      },
    ],
    eLinx: [
      {
        id: 'navModule-ePay',
        title: t('ePay') || '',
        path: '/elinx/pay',
        activeModuleId: 'ePay',
        subNavModules: [],
      },
      {
        id: 'navModule-eCollect',
        title: t('eCollect') || '',
        path: '/elinx/collect',
        activeModuleId: 'eCollect',
        subNavModules: [],
      },
      {
        id: 'navModule-eConnect',
        title: t('eConnect') || '',
        path: '/elinx/connect',
        activeModuleId: 'eConnect',
        subNavModules: [],
      },
      {
        id: 'navModule-eFlex',
        title: t('eFlex') || '',
        path: '/elinx/flex',
        activeModuleId: 'eFlex',
        subNavModules: [],
      },
      {
        id: 'navModule-elinx-history',
        title: t('eLinxHistory') || '',
        path: '/elinx-history',
        activeModuleId: 'elinxHistory',
        subNavModules: [],
      },
    ],
    payment: [
      {
        id: 'navModule-singlePayment',
        title: t('payment') || '',
        path: '/single-payment',
        activeModuleId: 'singlePayment',
        subNavModules: [],
      },
      {
        id: 'navModule-bulkPayment',
        title: t('groupPayment') || '',
        path: '/bulk-payment',
        activeModuleId: 'bulkPayment',
        subNavModules: [],
      },
      {
        id: 'navModule-billPayment',
        title: t('billPayment') || '',
        path: '/bill-payment',
        activeModuleId: 'billPayment',
        subNavModules: [],
      },
    ],
    collection: [
      {
        id: 'navModule-singleCollection',
        title: t('collection') || '',
        path: '/single-collection',
        activeModuleId: 'singleCollection',
        subNavModules: [],
      },
      {
        id: 'navModule-bulkCollection',
        title: t('groupCollection') || '',
        path: '/bulk-collection',
        activeModuleId: 'bulkCollection',
        subNavModules: [],
      },
    ],
    conversion: [],
    globalSend: [
      {
        id: 'navModule-globalSendHome',
        title: t('globalSendHome') || '',
        path: '/global-send/home',
        activeModuleId: 'globalSendHome',
        subNavModules: [],
      },
      {
        id: 'navModule-globalSendSend',
        title: t('globalSendSend') || '',
        path: '/global-send/send',
        activeModuleId: 'globalSendSend',
        subNavModules: [],
      },
      {
        id: 'navModule-globalSendRecipients',
        title: t('globalSendRecipients') || '',
        path: '/global-send/recipients',
        activeModuleId: 'globalSendRecipients',
        subNavModules: [],
      },
      {
        id: 'navModule-globalSendActivity',
        title: t('globalSendActivity') || '',
        path: '/global-send/activity',
        activeModuleId: 'globalSendActivity',
        subNavModules: [],
      },
      {
        id: 'navModule-globalSendFxEstimator',
        title: t('globalSendFxEstimator') || '',
        path: '/global-send/fxEstimator',
        activeModuleId: 'globalSendFxEstimator',
        subNavModules: [],
      },
    ],
    disputeManagement: [],
    contacts: [
      {
        id: 'navModule-addNewContact',
        title: t('addNewContact') || '',
        path: '/contacts/create',
        activeModuleId: 'addNewContact',
        subNavModules: [],
      },
      {
        id: 'navModule-contactAccounts',
        title: t('contactList') || '',
        path: '/contacts',
        activeModuleId: 'contactAccounts',
        subNavModules: [],
      },
      {
        id: 'navModule-ContactFileUpload',
        title: t('fileUpload') || '',
        path: '/contact-file-upload',
        activeModuleId: 'ContactFileUpload',
        subNavModules: [],
      },
      {
        id: 'navModule-ContactDownloadTemplate',
        title: t('downloadTemplate') || '',
        path: '/contact-download-template',
        activeModuleId: 'ContactdownloadTemplate',
        subNavModules: [],
      },
    ],
    upload: [],
    software: [
      {
        id: 'navModule-receivables-software',
        title: t('receivables') || '',
        path: '/receivables',
        activeModuleId: 'receivables',
        subNavModules: [],
      },
      {
        id: 'navModule-payables-software',
        title: t('payables') || '',
        path: '/payables',
        activeModuleId: 'payables',
        subNavModules: [],
      },
    ],
    transactions: [],
    statements: [
      {
        id: 'navModule-transactions-history',
        title: t('transactionsHistory') || '',
        path: '/transactions-history',
        activeModuleId: 'transactionsHistory',
        subNavModules: [],
      },
      {
        id: 'navModule-reports',
        title: t('reports') || '',
        path: '/reports',
        activeModuleId: 'reports',
        subNavModules: [],
      },
      {
        id: 'navModule-reconciliation',
        title: t('reconciliation') || '',
        path: '/reconciliation',
        activeModuleId: 'reconciliation',
        subNavModules: [],
      },
      {
        id: 'navModule-bank-verify-history',
        title: t('bankVerifyHistory') || '',
        path: '/bank-verify-history',
        activeModuleId: 'bankVerifyHistory',
        subNavModules: [],
      },
    ],
    integrations: [],
    settings: [],
    support: [
      {
        id: 'navModule-submitTicket',
        title: t('submitTicket') || '',
        path: '/create-tickets',
        activeModuleId: 'submitTicket',
        subNavModules: [],
      },
      {
        id: 'navModule-ticketsList',
        title: t('ticketsList') || '',
        path: '/tickets',
        activeModuleId: 'ticketsList',
        subNavModules: [],
      },
      {
        id: 'navModule-learningCentre',
        title: t('learningCentre') || '',
        path: '/learning-centre',
        activeModuleId: 'learningCentre',
        subNavModules: [],
      },
    ],
    // TODO: might need to re-enable
    // interestAccounts: [],
  };

  useEffect(() => {
    const subModule = subModuleMap[activeModule]?.find((module) => module.path === pathname);
    if (subModule) {
      onSubNavClick(subModule?.activeModuleId || '', subModule?.path || pathname, subModule?.title || '');
    }
    setOpenModule(activeModule);
  }, [pathname, activeModule]);

  const supportedModules: { [module in SupportedModules]: vp.NavModuleProps } = {
    dashboard: {
      id: 'navModule-dashboard',
      icon: 'dashboardSmall',
      activeIcon: 'dashboardSmallOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('overview'),
      isActive: activeModule === 'dashboard',
      isOpen: openModule === 'dashboard',
      subNavModules: getSubModules('dashboard'),
      onClick: () => {
        onNavClick('dashboard');
        setOpenModule('dashboard');
        history.push('/dashboard');
      },
      showNavModule: () => true,
    },
    myAccount: {
      id: 'navModule-myAccount',
      icon: 'briefcase',
      activeIcon: 'briefcaseOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('myAccount'),
      isActive: activeModule === 'myAccount',
      isOpen: openModule === 'myAccount',
      subNavModules: getSubModules('myAccount'),
      onClick: () => {
        onNavClick('myAccount');
        setOpenModule('myAccount');
      },
      showNavModule: () => true,
    },
    transactionApproval: {
      id: 'navModule-transactionApproval',
      icon: 'transactionApproval',
      activeIcon: 'transactionApprovalOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('transactionApproval'),
      isActive: activeModule === 'transactionApproval',
      isOpen: openModule === 'transactionApproval',
      onClick: () => {
        if (!lockedFeatures.TransactionApprovalWorkflowEnabled) {
          updateIsLocked(true, t('transactionApproval'));
        } else {
          onNavClick('transactionApproval');
          setOpenModule('transactionApproval');
        }
      },
      subNavModules: getSubModules('transactionApproval'),
      isLocked: () => !lockedFeatures.TransactionApprovalWorkflowEnabled,
      showNavModule: () => true,
    },
    subAccounts: {
      id: 'navModule-subAccount',
      icon: 'subAccount',
      activeIcon: 'subAccountOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('subAccounts'),
      isActive: activeModule === 'subAccounts',
      isOpen: openModule === 'subAccounts',
      subNavModules: getSubModules('subAccounts'),
      onClick: () => {
        if (SubAccountLocked) {
          updateIsLocked(true, t('subAccounts'));
        } else {
          onNavClick('subAccounts');
          setOpenModule('subAccounts');
        }
      },
      isLocked: () => SubAccountLocked,
      showNavModule: () => true,
    },
    // will we need this later? Who knows. Let's wait a bit.
    // interestAccounts: {
    //   id: 'navModule-interestAccounts',
    //   icon: 'briefcase',
    //   activeIcon: 'briefcaseOn',
    //   label: t('interestAccounts', { defaultValue: 'Interest Accounts' }),
    //   iconWidth: '20px',
    //   iconHeight: '20px',
    //   iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
    //   iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
    //   caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
    //   caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
    //   isActive: activeModule === 'interestAccounts',
    //   isOpen: openModule === 'interestAccounts',
    //   subNavModules: getSubModules('interestAccounts'),
    //   onClick: () => {
    //     if (!lockedFeatures.InterestAccountEnabled) {
    //       updateIsLocked(true, t('interestAccounts', { defaultValue: 'Interest Accounts' }));
    //     } else {
    //       onNavClick('interestAccounts');
    //       setOpenModule('interestAccounts');
    //       history.push('/interest-accounts');
    //     }
    //   },
    //   isLocked: () => !lockedFeatures.InterestAccountEnabled,
    //   showNavModule: () => false,
    // },
    eLinx: {
      label: t('eLinx'),
      id: 'navModule-eLinx',
      icon: 'eLinxE',
      iconWidth: '20px',
      iconHeight: '20px',
      activeIcon: 'eLinxE',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      isActive: activeModule === 'eLinx',
      isOpen: openModule === 'eLinx',
      subNavModules: getSubModules('eLinx'),
      onClick: () => {
        if (!lockedFeatures.PayLinkEnabled) {
          updateIsLocked(true, t('eLinx'));
        } else {
          onNavClick('eLinx');
          setOpenModule('eLinx');
        }
      },
      isLocked: () => !lockedFeatures.PayLinkEnabled,
      showNavModule: () => true,
    },
    clientAccounts: {
      id: 'navModule-clientAccount',
      icon: 'clientAccounts',
      activeIcon: 'clientAccountsOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('clientAccounts'),
      isActive: activeModule === 'clientAccounts',
      isOpen: openModule === 'clientAccounts',
      subNavModules: getSubModules('clientAccounts'),
      onClick: () => {
        if (!lockedFeatures.ClientAccountsEnabled) {
          updateIsLocked(true, t('clientAccounts'));
        } else {
          onNavClick('clientAccounts');
          setOpenModule('clientAccounts');
        }
      },
      isLocked: () => !lockedFeatures.ClientAccountsEnabled,
      showNavModule: () => true,
    },
    payment: {
      id: 'navModule-payment',
      icon: 'payment',
      activeIcon: 'paymentOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('payment'),
      isActive: activeModule === 'payment',
      isOpen: openModule === 'payment',
      subNavModules: getSubModules('payment'),
      onClick: () => {
        onNavClick('payment');
        setOpenModule('payment');
      },
      showNavModule: () => true,
    },
    collection: {
      id: 'navModule-collection',
      icon: 'collection',
      activeIcon: 'collectionOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('collection'),
      isActive: activeModule === 'collection',
      isOpen: openModule === 'collection',
      subNavModules: getSubModules('collection'),
      onClick: () => {
        onNavClick('collection');
        setOpenModule('collection');
      },
      showNavModule: () => true,
    },
    conversion: {
      id: 'navModule-conversion',
      icon: 'conversion',
      activeIcon: 'conversionOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('conversion'),
      isActive: activeModule === 'conversion',
      isOpen: openModule === 'conversion',
      subNavModules: getSubModules('conversion'),
      onClick: () => {
        onNavClick('conversion');
        setOpenModule('conversion');
      },
      showNavModule: () => false,
    },
    globalSend: {
      id: 'navModule-globalSend',
      icon: 'conversion',
      activeIcon: 'conversionOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('globalSend'), // change to global send
      isActive: activeModule === 'globalSend',
      isOpen: openModule === 'globalSend',
      subNavModules: getSubModules('globalSend'),
      onClick: () => {
        onNavClick('globalSend');
        setOpenModule('globalSend');
      },
      showNavModule: () => true,
    },
    disputeManagement: {
      id: 'navModule-disputeManagement',
      icon: 'scheduled',
      activeIcon: 'scheduledOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('disputeManagement', { defaultValue: 'Dispute Management' }),
      isActive: activeModule === 'disputeManagement',
      isOpen: openModule === 'disputeManagement',
      subNavModules: getSubModules('disputeManagement'),
      onClick: () => {
        onNavClick('disputeManagement');
        setOpenModule('disputeManagement');
        history.push('/dispute-management');
      },
      showNavModule: () => true,
    },
    contacts: {
      id: 'navModule-contacts',
      icon: 'clientAccounts',
      activeIcon: 'clientAccountsOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('contacts'),
      isActive: activeModule === 'contacts',
      isOpen: openModule === 'contacts',
      subNavModules: getSubModules('contacts'),
      onClick: () => {
        onNavClick('contacts');
        setOpenModule('contacts');
      },
      showNavModule: () => true,
    },
    upload: {
      id: 'navModule-upload',
      icon: 'upload',
      activeIcon: 'uploadOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('bulkFileUpload'),
      isActive: activeModule === 'upload',
      isOpen: openModule === 'upload',
      subNavModules: getSubModules('upload'),
      onClick: () => {
        history.push('/file-upload-history');
      },
      showNavModule: () => true,
    },
    software: {
      id: 'navModule-software',
      icon: 'money',
      activeIcon: 'moneyOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('accountingSoftware'),
      isActive: activeModule === 'software',
      isOpen: openModule === 'software',
      subNavModules: getSubModules('software'),
      onClick: () => {
        onNavClick('software');
        setOpenModule('software');
      },
      isLocked: () => false,
      showNavModule: () => true,
    },
    transactions: {
      id: 'navModule-scheduledTransactions',
      icon: 'scheduled',
      activeIcon: 'scheduledOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('scheduledTransactions'),
      isActive: activeModule === 'transactions',
      isOpen: openModule === 'transactions',
      subNavModules: getSubModules('transactions'),
      onClick: () => {
        onNavClick('transactions');
        setOpenModule('transactions');
        history.push('/scheduled-transactions');
      },
      showNavModule: () => true,
    },
    statements: {
      id: 'navModule-statements',
      icon: 'invoice',
      activeIcon: 'invoiceOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('statementsAndReports'),
      isActive: activeModule === 'statements',
      isOpen: openModule === 'statements',
      subNavModules: getSubModules('statements'),
      onClick: () => {
        onNavClick('statements');
        setOpenModule('statements');
      },
      showNavModule: () => true,
    },
    integrations: {
      id: 'navModule-integrations',
      icon: 'developer',
      activeIcon: 'developerOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('integrations'),
      isActive: activeModule === 'integrations',
      isOpen: openModule === 'integrations',
      subNavModules: getSubModules('integrations'),
      onClick: () => {
        onNavClick('integrations');
        setOpenModule('integrations');
        history.push('/integrations');
      },
      showNavModule: () => true,
    },
    settings: {
      id: 'navModule-settings',
      icon: 'setting',
      activeIcon: 'settingOn',
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      label: t('settings'),
      isActive: activeModule === 'settings',
      isOpen: openModule === 'settings',

      subNavModules: getSubModules('settings'),
      onClick: () => {
        onNavClick('settings');
        setOpenModule('settings');
        history.push('/settings');
      },
      showNavModule: () => true,
    },
    support: {
      id: 'navModule-support',
      icon: 'support',
      activeIcon: 'supportOn',
      label: t('support'),
      iconWidth: '20px',
      iconHeight: '20px',
      iconColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      iconColorOn: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColor: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      caretColorClosed: vp.getLuminance(secondaryColor) > 0.5 ? 'primary-dark' : 'primary-light',
      isActive: activeModule === 'support',
      isOpen: openModule === 'support',
      subNavModules: getSubModules('support'),
      onClick: () => {
        onNavClick('support');
        setOpenModule('support');
      },
      showNavModule: () => true,
    },
    users: {
      id: 'navModule-users',
      icon: 'homeDoor',
      iconColorOn: 'primary-dark',
      iconWidth: '20px',
      iconHeight: '20px',
      label: t('users'),
      isActive: activeModule === 'users',
      isOpen: openModule === 'users',
      subNavModules: getSubModules('users'),
      onClick: () => {
        onNavClick('users');
        setOpenModule('users');
        history.push('/users');
      },
      showNavModule: () => true,
    },
  };

  return (
    <Box jc="sa" height="auto" gap="md" flx="col" width="100%">
      <div className={styles.container}>
        {buildModuleList().map((props) => (
          <NavModule key={props.id} {...props} expandMenu={expandMenu} />
        ))}
      </div>
    </Box>
  );

  // This function is used to check if all subModules are locked the parent should be locked
  function checkIsLocked(subModules: string): boolean {
    return !getSubModules(subModules).find((v: vp.NavModuleProps) => v.isLocked && !v.isLocked());
  }

  function buildModuleList(): vp.NavModuleProps[] {
    const initList: vp.NavModuleProps[] = [];
    const moduleEntries = (Object.entries(supportedModules) as [SupportedModules, vp.NavModuleProps][]).filter(
      ([, module]) => module.showNavModule(),
    );

    return moduleEntries.reduce((accumulator, [moduleKey, moduleProps]) => {
      const modulePropsCopy = { ...moduleProps };
      const { isEnabled } = useModuleFeatureFlag(moduleKey);
      modulePropsCopy.subNavModules = getSubModules(moduleKey) ?? [];
      const isParent = PARENT_MODULES.has(moduleKey);
      const hasChildren = modulePropsCopy.subNavModules.length > 0;
      return isEnabled && (!isParent || hasChildren) ? [...accumulator, modulePropsCopy] : accumulator;
    }, initList);
  }
};

export default NavigationMenu;
